import { useEffect } from 'react';
import { Business } from '@/types/business';

interface DynamicMetaTagsProps {
  business?: Business;
  isBusinessPage?: boolean;
}

export default function DynamicMetaTags({ business, isBusinessPage = false }: DynamicMetaTagsProps) {
  useEffect(() => {
    if (isBusinessPage && business) {
      // Enhanced SEO-friendly meta tags for business page
      const title = `${business.name} | ${business.category} in ${business.city} - GoodVote`;
      const description = `${business.name} - ${business.category} in ${business.city}. View ratings, reviews, hours, and contact info. Address: ${business.address}. Community-driven business reviews on GoodVote.`;
      const imageUrl = business.image || 'https://goodvote.ge/placeholder.svg';
      const url = `https://goodvote.ge/business/${business.id}`;

      // Update page title
      document.title = title;

      // Enhanced meta tags for better SEO
      updateMetaTag('property', 'og:title', title);
      updateMetaTag('property', 'og:description', description);
      updateMetaTag('property', 'og:image', imageUrl);
      updateMetaTag('property', 'og:url', url);
      updateMetaTag('property', 'og:type', 'place');
      updateMetaTag('property', 'og:site_name', 'GoodVote');
      updateMetaTag('property', 'place:location:latitude', business.latitude?.toString() || '');
      updateMetaTag('property', 'place:location:longitude', business.longitude?.toString() || '');
      
      updateMetaTag('name', 'twitter:card', 'summary_large_image');
      updateMetaTag('name', 'twitter:title', title);
      updateMetaTag('name', 'twitter:description', description);
      updateMetaTag('name', 'twitter:image', imageUrl);
      
      updateMetaTag('name', 'description', description);
      updateMetaTag('name', 'keywords', `${business.name}, ${business.category}, ${business.city}, reviews, ratings, ${business.address}`);
      updateMetaTag('name', 'author', 'GoodVote');
      updateMetaTag('name', 'robots', 'index, follow');
      
      // Canonical URL
      updateLinkTag('canonical', url);
    } else {
      // Restore default meta tags for main page
      const defaultTitle = 'GoodVote - Find the Best, Skip the Rest';
      const defaultDescription = 'A community-driven platform for honest business reviews. Upvote or downvote restaurants, cafes, gyms, and more based on real experiences.';
      const defaultImage = 'https://nartcbzfahbmkgsysyur.supabase.co/storage/v1/object/public/images/og-image.png';
      const defaultUrl = 'https://goodvote.ge';

      document.title = defaultTitle;

      updateMetaTag('property', 'og:title', defaultTitle);
      updateMetaTag('property', 'og:description', defaultDescription);
      updateMetaTag('property', 'og:image', defaultImage);
      updateMetaTag('property', 'og:url', defaultUrl);
      updateMetaTag('property', 'og:type', 'website');
      
      updateMetaTag('name', 'twitter:title', defaultTitle);
      updateMetaTag('name', 'twitter:description', defaultDescription);
      updateMetaTag('name', 'twitter:image', defaultImage);
      
      updateMetaTag('name', 'description', defaultDescription);
      
      // Canonical URL for homepage
      updateLinkTag('canonical', defaultUrl);
    }
  }, [business, isBusinessPage]);

  return null; // This component doesn't render anything
}

function updateMetaTag(attribute: string, attributeValue: string, content: string) {
  if (!content) return; // Don't create empty meta tags
  
  let element = document.querySelector(`meta[${attribute}="${attributeValue}"]`) as HTMLMetaElement;
  
  if (element) {
    element.content = content;
  } else {
    // Create new meta tag if it doesn't exist
    element = document.createElement('meta');
    element.setAttribute(attribute, attributeValue);
    element.content = content;
    document.head.appendChild(element);
  }
}

function updateLinkTag(rel: string, href: string) {
  if (!href) return;
  
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  
  if (element) {
    element.href = href;
  } else {
    // Create new link tag if it doesn't exist
    element = document.createElement('link');
    element.rel = rel;
    element.href = href;
    document.head.appendChild(element);
  }
}